const PRODUCTION_HOSTS = ['saayeeem.github.io'];
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

let analyticsStarted = false;
let analyticsCleanups = [];
const seenSections = new Set();

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isProductionHost() {
  return PRODUCTION_HOSTS.includes(window.location.hostname);
}

function isDoNotTrackEnabled() {
  return (
    window.navigator.doNotTrack === '1' ||
    window.doNotTrack === '1' ||
    window.navigator.msDoNotTrack === '1'
  );
}

function ensureGtagInitialized() {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false
  });
}

function loadGaScript() {
  if (document.getElementById('ga4-tag-script')) {
    return;
  }

  const gaScript = document.createElement('script');
  gaScript.id = 'ga4-tag-script';
  gaScript.async = true;
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(gaScript);
}

function buildPagePath() {
  return `${window.location.pathname}${window.location.hash || ''}`;
}

export function trackPageView(pagePath = buildPagePath()) {
  if (!isBrowser() || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: pagePath
  });
}

export function trackEvent(eventName, params = {}) {
  if (!isBrowser() || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', eventName, params);
}

function setupRouteTracking() {
  const onPageChange = () => {
    trackPageView();
  };

  trackPageView();
  window.addEventListener('hashchange', onPageChange);
  window.addEventListener('popstate', onPageChange);

  return () => {
    window.removeEventListener('hashchange', onPageChange);
    window.removeEventListener('popstate', onPageChange);
  };
}

function setupSectionTracking() {
  if (typeof window.IntersectionObserver !== 'function') {
    return () => {};
  }

  const sections = Array.from(document.querySelectorAll('section[id]'));
  if (!sections.length) {
    return () => {};
  }

  const observer = new window.IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const sectionId = entry.target.getAttribute('id');
        if (!sectionId || seenSections.has(sectionId)) {
          return;
        }

        seenSections.add(sectionId);
        trackEvent('section_view', { section_id: sectionId });
      });
    },
    {
      threshold: 0.55
    }
  );

  sections.forEach((section) => observer.observe(section));

  return () => {
    observer.disconnect();
  };
}

function setupClickTracking() {
  const onDocumentClick = (event) => {
    const anchor = event.target.closest('a');
    if (!anchor) {
      return;
    }

    const href = (anchor.getAttribute('href') || '').trim();
    if (!href) {
      return;
    }

    const normalizedHref = href.toLowerCase();
    const linkText = (anchor.textContent || '').trim().slice(0, 100);

    if (normalizedHref.startsWith('mailto:')) {
      trackEvent('contact_mail_click', {
        mail_target: href.replace('mailto:', ''),
        link_text: linkText
      });
      return;
    }

    if (
      anchor.hasAttribute('download') ||
      (/assets\/cv\//i.test(href) && /\.pdf$/i.test(href))
    ) {
      trackEvent('cv_download', {
        file_path: href,
        link_text: linkText
      });
      return;
    }

    let resolvedUrl;
    try {
      resolvedUrl = new URL(href, window.location.origin);
    } catch (err) {
      return;
    }

    const host = resolvedUrl.hostname.toLowerCase();
    const eventPayload = {
      link_url: resolvedUrl.href,
      link_host: host,
      link_text: linkText
    };

    if (host.includes('medium.com')) {
      trackEvent('medium_article_click', eventPayload);
      return;
    }

    if (host.includes('scholar.google.')) {
      trackEvent('google_scholar_click', eventPayload);
      return;
    }

    if (host.includes('github.com')) {
      trackEvent('github_click', eventPayload);
      return;
    }

    if (anchor.closest('.publication-card') && /^https?:$/i.test(resolvedUrl.protocol)) {
      trackEvent('publication_outbound_click', eventPayload);
    }
  };

  document.addEventListener('click', onDocumentClick, true);

  return () => {
    document.removeEventListener('click', onDocumentClick, true);
  };
}

export function startAnalyticsTracking() {
  if (!isBrowser()) {
    return () => {};
  }

  if (analyticsStarted) {
    return () => {};
  }

  if (!GA_MEASUREMENT_ID) {
    return () => {};
  }

  if (!isProductionHost() || isDoNotTrackEnabled()) {
    return () => {};
  }

  loadGaScript();
  ensureGtagInitialized();

  analyticsCleanups = [
    setupRouteTracking(),
    setupSectionTracking(),
    setupClickTracking()
  ];

  analyticsStarted = true;

  return () => {
    analyticsCleanups.forEach((cleanup) => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    });
    analyticsCleanups = [];
    analyticsStarted = false;
    seenSections.clear();
  };
}
